import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { interviewApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getUserProfileId } from '../../utils/userProfile';
import {
  COURSE_INTERVIEWS_CHANGED,
  courseInterviewSlotBadge,
  extractInterviewList,
  isCourseInterviewSlotUsed,
  mapCourseSessionStatuses,
} from '../../utils/courseInterviewSessions';

type CourseScheduleInterviewsProps = {
  courseId: number;
  courseSlug: string;
  courseTitle: string;
  interviewPackageCount: number;
  compact?: boolean;
};

const CourseScheduleInterviews: React.FC<CourseScheduleInterviewsProps> = ({
  courseId,
  courseSlug,
  courseTitle,
  interviewPackageCount,
  compact = false,
}) => {
  const { user, isAuthenticated } = useAuth();
  const profileId = getUserProfileId(user);
  const [sessions, setSessions] = useState(() =>
    mapCourseSessionStatuses([], courseId, interviewPackageCount, courseTitle, courseSlug)
  );

  useEffect(() => {
    if (!isAuthenticated || !profileId || courseId <= 0) return undefined;
    let cancelled = false;
    const load = () => {
      interviewApi.getByUser(profileId)
        .then((r) => {
          if (cancelled) return;
          const rows = extractInterviewList(r.data);
          setSessions(mapCourseSessionStatuses(rows, courseId, interviewPackageCount, courseTitle, courseSlug));
        })
        .catch(() => undefined);
    };
    load();
    const onChange = () => load();
    window.addEventListener(COURSE_INTERVIEWS_CHANGED, onChange);
    return () => {
      cancelled = true;
      window.removeEventListener(COURSE_INTERVIEWS_CHANGED, onChange);
    };
  }, [courseId, courseSlug, courseTitle, interviewPackageCount, isAuthenticated, profileId]);

  if (sessions.length === 0) return null;

  const shell = compact
    ? 'rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-3'
    : 'rounded-2xl border border-indigo-200 bg-indigo-50 p-4';

  return (
    <div className={shell}>
      <p className="text-xs font-semibold uppercase tracking-wide text-indigo-800">Schedule interviews</p>
      <p className="mt-1 text-xs text-indigo-900/90">
        Schedule your first review before starting the course to map gaps and set a plan.
      </p>
      <ul className="mt-2 space-y-1">
        {sessions.map(({ session, status }) => {
          const used = isCourseInterviewSlotUsed(status);
          if (used && status) {
            const badge = courseInterviewSlotBadge(status);
            const done = badge === 'Completed';
            return (
              <li
                key={session}
                className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm text-indigo-900 bg-white/70 border border-indigo-100"
              >
                <span className="font-medium">Interview {session}</span>
                <span className={`inline-flex items-center gap-1 text-xs font-semibold ${done ? 'text-emerald-700' : 'text-indigo-700'}`}>
                  {done ? <CheckCircle className="h-3.5 w-3.5" /> : null}
                  {badge}
                </span>
              </li>
            );
          }
          return (
            <li key={session}>
              <Link
                to={`/interviews?contentCourseId=${courseId}&courseSlug=${encodeURIComponent(courseSlug)}&courseTitle=${encodeURIComponent(courseTitle)}&session=${session}`}
                className="block rounded-lg px-2 py-1.5 text-sm font-medium text-indigo-900 hover:bg-white"
              >
                Schedule interview {session}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default CourseScheduleInterviews;
